"use strict";

/* ------------------------------------------------------------------ *
 * Mappers 21, 23 and 22: Konami VRC4 (VRC4a / VRC4c, VRC2b / VRC4e)
 * and VRC2 (VRC2a)
 * ------------------------------------------------------------------ *
 * The VRC2 and the VRC4 are the same Konami ASIC with the same register
 * layout; what differs is how the board wires the CPU address lines to the
 * chip's two register-select lines, and whether the chip has an IRQ.
 *
 * Two address lines pick one of four registers in every $1000 page, and the
 * wiring decides which addresses those are (NESdev, "VRC2 and VRC4"):
 *
 *   variant   select lines   registers live at
 *   VRC2a      A1, A0        $x000, $x002, $x001, $x003   (mapper 22)
 *   VRC4a      A1, A2        $x000, $x002, $x004, $x006   (mapper 21)
 *   VRC4c      A6, A7        $x000, $x040, $x080, $x0C0   (mapper 21)
 *   VRC2b      A0, A1        $x000, $x001, $x002, $x003   (mapper 23)
 *   VRC4e      A2, A3        $x000, $x004, $x008, $x00C   (mapper 23)
 *
 * iNES mapper 21 covers both VRC4a and VRC4c, and mapper 23 both VRC2b and
 * VRC4e; the two wirings of each pair do not overlap, so one decoder that
 * answers to both serves every board: the register number is the OR of the
 * two address-line pairs, exactly as Mesen does for these mappers. Mapper 22
 * is always a VRC2a.
 *
 * Mapper 23 carries the IRQ of both its variants: the VRC4e has the same
 * cycle-prescaler IRQ as the VRC4, and the VRC2b has no clock source at all,
 * yet the same register writes arm it -- ROMs written for the VRC2b never
 * touch $F000-$F003, so the decoder keeps the IRQ armed-able either way.
 *
 * The registers themselves (numbered 0-3 in each page):
 *
 *   $8000  PRG Select 0   -- the 8K window at $8000, or at $C000 when the
 *                            swap mode is set
 *   $9000  nametable arrangement (0 vertical, 1 horizontal, 2 one-screen
 *                            lower, 3 one-screen upper; the VRC2 only has
 *                            the first two)
 *   $9002  PRG swap mode (VRC4 only): bit 1 swaps the $8000 and $C000
 *                            windows, bit 0 is the WRAM control
 *   $A000  PRG Select 1   -- the 8K window at $A000
 *   $B000  eight 1K CHR windows, each with a low (4 bits) and a high
 *          (5 bits) register
 *   $F000  IRQ latch low, latch high, control, acknowledge (VRC4 only)
 *
 * The VRC2 also has a one-bit latch at $6000-$6FFF -- the "microwire"
 * interface that was meant to reach a serial EEPROM. Konami never used it,
 * and on the mapper 22 board the EEPROM data pin is tied to ground, but
 * games still read back what they wrote, so the latch is modelled.
 * ------------------------------------------------------------------ */

// VRC4a selects its registers with A1 and A2, the VRC4c revision moved them
// to A6 and A7. ORing both pairs answers to either board.
function vrc4Register(address) {
  var a0 = ((address >> 1) & 1) | ((address >> 6) & 1);
  var a1 = ((address >> 2) & 1) | ((address >> 7) & 1);
  return (address & 0xff00) | (a1 << 1) | a0;
}

// The VRC2a takes its register number from A0 and A1 with A0 as the low bit:
// register 0 is at $x000, register 1 at $x002, register 2 at $x001 and
// register 3 at $x003.
function vrc2aRegister(address) {
  var a0 = (address >> 1) & 1;
  var a1 = address & 1;
  return (address & 0xff00) | (a1 << 1) | a0;
}

// Mapper 23 is one mapping ORed over its two wirings: the VRC2b takes A0 and
// A1 (registers in natural order, the other way round from the VRC2a), and
// the VRC4e moved them to A2 and A3.
function vrc23Register(address) {
  var a0 = ((address >> 0) & 1) | ((address >> 2) & 1);
  var a1 = ((address >> 1) & 1) | ((address >> 3) & 1);
  return (address & 0xff00) | (a1 << 1) | a0;
}

function register(Mappers) {
  // The two boards share every line of code; `this.variant` says which one is
  // running ("vrc4" or "vrc2").
  var shared = {
    reset: function () {
      Mappers[0].prototype.reset.apply(this);

      // PRG: two switchable 8K windows ($8000 and $A000) and the last two 8K
      // banks fixed at $C000 and $E000, unless the VRC4 swap mode moves the
      // $8000 register to $C000.
      this.prgBank0 = 0; // 5 bits
      this.prgBank1 = 0; // 5 bits
      this.prgSwap = false; // $9002 bit 1

      // CHR: eight 9-bit 1K banks for PPU $0000-$1FFF, each split into a low
      // 4-bit register and a high 5-bit register.
      this.chrLow = [0, 0, 0, 0, 0, 0, 0, 0];
      this.chrHigh = [0, 0, 0, 0, 0, 0, 0, 0];

      // $9000 nametable arrangement, in the chip's own encoding.
      this.mirroring = 0;

      // The one-bit microwire latch at $6000-$6FFF (VRC2).
      this.microwireLatch = 0;

      // IRQ: an 8-bit up-counting counter behind a CPU-cycle prescaler (VRC4).
      this.irqLatch = 0;
      this.irqCounter = 0;
      this.irqMode = 0; // $F002 bit 2: 0 = scanline prescaler, 1 = cycle
      this.irqEnabled = false; // $F002 bit 1
      this.irqEnableAck = false; // $F002 bit 0: copied into E by an ack
      this.irqPrescaler = 341; // scanline mode: drawn down 3 per CPU cycle
    },

    // The register number of a write, in the chip's own numbering.
    translate: function (address) {
      if (this.variant === "vrc2") {
        return vrc2aRegister(address);
      }
      if (this.variant === "vrc23") {
        return vrc23Register(address);
      }
      return vrc4Register(address);
    },

    write: function (address, value) {
      var isVrc2Family = this.variant === "vrc2" || this.variant === "vrc23";

      if (address < 0x8000) {
        if (isVrc2Family && !this.hasWorkRam() && address >= 0x6000) {
          // The microwire latch. The VRC2 has no RAM of its own on the
          // mapper 22 board, so this window is the latch; a board that does
          // carry RAM (and says so in its header) keeps it.
          //
          // Only $6000 and up is the latch: the work RAM and the stack below
          // it belong to the CPU, and a write that never reached them left
          // the game's stack full of zeroes.
          this.microwireLatch = value & 0x01;
          return;
        }
        // $6000-$7FFF is the cartridge's work RAM (or open bus).
        Mappers[0].prototype.write.apply(this, arguments);
        return;
      }

      // The register number, with the page it belongs to.
      var addr = this.translate(address) & 0xf00f;

      if (addr >= 0x8000 && addr <= 0x8006) {
        // PRG Select 0: the 8K window at $8000, or at $C000 under the swap
        // mode.
        this.prgBank0 = value & 0x1f;
        this.updatePrgBanks();
      } else if (
        addr >= 0x9000 &&
        addr <=
          (this.variant === "vrc4" || this.variant === "vrc2" ? 0x9001 : 0x9003)
      ) {
        // Nametable arrangement. Mapper 22's VRC2a and the VRC4 both decode
        // two registers into it; the VRC2a only offers vertical and
        // horizontal, so its second bit is ignored. The mapper 23 decoder is
        // the ORed one, so it also keeps the second range reachable and uses
        // both bits, as Mesen's heuristic does.
        this.mirroring = value & (this.variant === "vrc2" ? 0x01 : 0x03);
        this.applyMirroring();
      } else if (this.variant === "vrc4" && addr >= 0x9002 && addr <= 0x9003) {
        // PRG swap mode (bit 1) and WRAM control (bit 0). The WRAM bit is
        // decoded but the window is always backed by RAM here: a game that
        // uses the RAM enables it first, and one that does not never reads
        // it back.
        this.prgSwap = ((value >> 1) & 0x01) !== 0;
        this.updatePrgBanks();
      } else if (addr >= 0xa000 && addr <= 0xa006) {
        // PRG Select 1: the 8K window at $A000.
        this.prgBank1 = value & 0x1f;
        this.updatePrgBanks();
      } else if (addr >= 0xb000 && addr <= 0xe006) {
        // CHR: eight 1K windows, each with a low and a high register. The
        // register number picks the window, the low bit of the address picks
        // which half of the nine bits is being written.
        var window = (((addr >> 12) & 0x07) - 3) * 2 + ((addr >> 1) & 0x01);
        if ((addr & 0x01) === 0) {
          this.chrLow[window] = value & 0x0f;
        } else {
          this.chrHigh[window] = value & 0x1f;
        }
        this.updateChrBanks();
      } else if (addr === 0xf000) {
        // IRQ latch, low nibble.
        this.irqLatch = (this.irqLatch & 0xf0) | (value & 0x0f);
      } else if (addr === 0xf001) {
        // IRQ latch, high nibble.
        this.irqLatch = (this.irqLatch & 0x0f) | ((value & 0x0f) << 4);
      } else if (addr === 0xf002) {
        // IRQ control: M (bit 2), E (bit 1), A (bit 0). Any write
        // acknowledges the pending IRQ and resets the prescaler; an enable
        // that went high also reloads the counter from the latch.
        this.irqMode = (value >> 2) & 1;
        this.irqEnabled = ((value >> 1) & 1) !== 0;
        this.irqEnableAck = (value & 1) !== 0;
        this.irqPrescaler = 341;
        if (this.irqEnabled) {
          this.irqCounter = this.irqLatch;
        }
      } else if (addr === 0xf003) {
        // Acknowledge: the A bit is copied into E, so writing $F003 with A
        // set in the last control write keeps the IRQ armed ("repeated"), or
        // disarms it ("one-shot") when A was clear.
        this.irqEnabled = this.irqEnableAck;
      }
    },

    // Reads from the microwire window give back the latch bit, with the
    // open bus in the upper bits: a read of $6000 returns $60|latch.
    load: function (address) {
      if (
        (this.variant === "vrc2" || this.variant === "vrc23") &&
        !this.hasWorkRam() &&
        address >= 0x6000 &&
        address < 0x7000
      ) {
        return 0x60 | this.microwireLatch;
      }
      return Mappers[0].prototype.load.apply(this, arguments);
    },

    // Whether the cartridge carries RAM in the $6000 window. The VRC2's own
    // RAM decoding only reaches $6000-$6FFF; a board with 8K of RAM says so
    // in its header.
    hasWorkRam: function () {
      return this.nes.rom.batteryRam === true;
    },

    // The VRC4 counts CPU cycles (through a 114/114/113 prescaler), not PPU
    // scanlines, so NES.frame() hands it the real cycle count per CPU burst.
    clockCpuCycles: function (cycles) {
      // The VRC2a of mapper 22 has no IRQ at all (microwire only).
      if (this.variant === "vrc2") {
        return;
      }

      // Disabled, the VRC4 does not clock either the prescaler or the counter.
      if (!this.irqEnabled) {
        return;
      }

      if (this.irqMode === 1) {
        for (var i = 0; i < cycles; i++) {
          this.irqStep();
        }
        return;
      }

      // Scanline mode: a 114/114/113-cycle prescaler. nesdev's simple form
      // keeps the countdown in one value: it starts at 341 and drops 3 per
      // cycle, and each underflow is one 113.67-cycle scanline and clocks
      // the counter.
      var prescaler = this.irqPrescaler;
      for (var j = 0; j < cycles; j++) {
        prescaler -= 3;
        if (prescaler <= 0) {
          prescaler += 341;
          this.irqStep();
        }
      }
      this.irqPrescaler = prescaler;
    },

    // One counter clock: increment, and a wrap from $FF reloads the latch and
    // trips the IRQ line. The line stays asserted (the CPU re-requests on
    // every wrap) until the game acknowledges.
    irqStep: function () {
      if (this.irqCounter === 0xff) {
        this.irqCounter = this.irqLatch;
        if (this.irqEnabled) {
          this.nes.cpu.requestIrq(this.nes.cpu.IRQ_NORMAL);
        }
      } else {
        this.irqCounter++;
      }
    },

    updatePrgBanks: function () {
      var count8k = this.nes.rom.romCount * 2; // romCount is 16K banks
      this.load8kRomBank(count8k - 1, 0xe000); // last, fixed
      if (this.prgSwap) {
        this.load8kRomBank(count8k - 2, 0x8000);
        this.load8kRomBank(this.prgBank0, 0xc000);
      } else {
        this.load8kRomBank(this.prgBank0, 0x8000);
        this.load8kRomBank(count8k - 2, 0xc000);
      }
      this.load8kRomBank(this.prgBank1, 0xa000);
    },

    updateChrBanks: function () {
      for (var i = 0; i < 8; i++) {
        var page = this.chrLow[i] | (this.chrHigh[i] << 4);
        if (this.variant === "vrc2") {
          // The VRC2a ignores the low bit of the CHR register, so the page
          // number has to be halved.
          page >>= 1;
        }
        this.load1kVromBank(page, i * 0x400);
      }
    },

    applyMirroring: function () {
      var types = this.nes.ppu.MIRRORING_TYPES;
      var rom = this.nes.rom;
      switch (this.mirroring) {
        case 0:
          this.setMirroring(rom.VERTICAL_MIRRORING);
          break;
        case 1:
          this.setMirroring(rom.HORIZONTAL_MIRRORING);
          break;
        case 2:
          this.setMirroring(types.SINGLE_SCREEN);
          break;
        default:
          this.setMirroring(types.SINGLE_SCREEN_ALT);
          break;
      }
    },

    loadROM: function () {
      if (!this.nes.rom.valid) {
        throw new Error("VRC2/VRC4: Invalid ROM! Unable to load.");
      }

      this.reset();
      this.loadCHRROM();
      this.loadBatteryRam();
      this.loadTrainer();

      // Power-on bank layout: every register is zero, the last 8K bank is
      // fixed at $E000 (where the reset vector lives) and the second-to-last
      // at $C000.
      this.updatePrgBanks();
      this.updateChrBanks();

      // Seed the arrangement from the header: a board that never writes
      // $9000 keeps the declared arrangement.
      this.mirroring =
        this.nes.rom.getMirroringType() === this.nes.rom.HORIZONTAL_MIRRORING
          ? 1
          : 0;
      this.applyMirroring();

      this.resetIrqHandler();
    },

    toJSON: function () {
      var s = Mappers[0].prototype.toJSON.apply(this);
      s.prgBank0 = this.prgBank0;
      s.prgBank1 = this.prgBank1;
      s.prgSwap = this.prgSwap;
      s.chrLow = this.chrLow.slice();
      s.chrHigh = this.chrHigh.slice();
      s.mirroring = this.mirroring;
      s.microwireLatch = this.microwireLatch;
      s.irqLatch = this.irqLatch;
      s.irqCounter = this.irqCounter;
      s.irqMode = this.irqMode;
      s.irqEnabled = this.irqEnabled;
      s.irqEnableAck = this.irqEnableAck;
      s.irqPrescaler = this.irqPrescaler;
      return s;
    },

    fromJSON: function (s) {
      Mappers[0].prototype.fromJSON.apply(this, arguments);
      this.prgBank0 = s.prgBank0;
      this.prgBank1 = s.prgBank1;
      this.prgSwap = s.prgSwap;
      this.chrLow = s.chrLow.slice();
      this.chrHigh = s.chrHigh.slice();
      this.mirroring = s.mirroring;
      this.microwireLatch = s.microwireLatch;
      this.irqLatch = s.irqLatch;
      this.irqCounter = s.irqCounter;
      this.irqMode = s.irqMode;
      this.irqEnabled = s.irqEnabled;
      this.irqEnableAck = s.irqEnableAck;
      this.irqPrescaler = s.irqPrescaler;
      // The CPU's copy of the ROM windows and the PPU's copy of the pattern
      // windows are not part of the mapper's state, so re-derive them.
      this.updatePrgBanks();
      this.updateChrBanks();
      this.applyMirroring();
    },
  };

  function install(number, variant) {
    Mappers[number] = function (nes) {
      this.nes = nes;
      this.variant = variant;
    };
    Mappers[number].prototype = new Mappers[0]();
    for (var key in shared) {
      Mappers[number].prototype[key] = shared[key];
    }

    // The board drives its own nametable arrangement: the $9000 register, not
    // the iNES header, owns it. loadROM still seeds the register from the
    // header so a board that never writes $9000 keeps the declared
    // arrangement.
    Mappers[number].prototype.ownsMirroring = true;

    // Only the VRC4 counts CPU cycles for its IRQ, plus mapper 23 whose
    // VRC4e variant owns the same IRQ (the VRC2b never arms it).
    Mappers[number].prototype.watchesCpuCycles = variant !== "vrc2";
  }

  // Mapper 21: VRC4a and VRC4c, the two wirings of the same chip.
  install(21, "vrc4");
  // Mapper 22: VRC2a.
  install(22, "vrc2");
  // Mapper 23: VRC2b and VRC4e, the other two wirings of the same chip.
  install(23, "vrc23");
}

module.exports = { register: register };
